import React from 'react';

interface MintoLogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showSubtitle?: boolean;
  subtitleClassName?: string;
}

// ── Base SVG canvas ──────────────────────────────────────
const W = 280;   // viewBox width
const H = 80;    // viewBox height
const FS = 68;   // font-size of "minto" text
const BL = 70;   // baseline y

// ── "i" dot position (adjust DX if dot drifts left/right) ─
const DX = 74;  // x-center — "i" dot position
const DY = 20;  // y-center
const DR = 13;  // radius — bigger screw

const DURATION = '4.5s';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@900&display=swap');

  /* Phillips cross: fade in → spin 2× → fade out */
  @keyframes slotSpin {
    0%,  12% { opacity: 0; transform: rotate(0deg);   }
    22%      { opacity: 1; transform: rotate(0deg);   }
    68%      { opacity: 1; transform: rotate(720deg); }
    78%      { opacity: 0; transform: rotate(720deg); }
    100%     { opacity: 0; transform: rotate(720deg); }
  }
  .ml-slot {
    transform-box: view-box;
    transform-origin: ${DX}px ${DY}px;
    animation: slotSpin ${DURATION} ease-in-out infinite;
  }

  /* Smiley: pop in after screw fades */
  @keyframes smileyPop {
    0%,  74% { opacity: 0; transform: scale(0.5); }
    88%      { opacity: 1; transform: scale(1.1); }
    94%      { opacity: 1; transform: scale(0.95);}
    100%     { opacity: 1; transform: scale(1);   }
  }
  .ml-smiley {
    transform-box: view-box;
    transform-origin: ${DX}px ${DY}px;
    animation: smileyPop ${DURATION} ease-out infinite;
  }
`;

const MintoLogo: React.FC<MintoLogoProps> = ({
  size = 'md',
  className = '',
  showSubtitle = false,
  subtitleClassName,
}) => {
  const sc = { sm: 0.5, md: 0.65, lg: 1.0 }[size]!;

  return (
    <span
      className={`inline-flex flex-col items-start ${className}`}
      aria-label="minto"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={Math.round(W * sc)}
        height={Math.round(H * sc)}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', overflow: 'hidden' }}
      >
        <defs>
          <style>{css}</style>
          {/* Clip out the native "i" dot area from the text — no white box needed */}
          <clipPath id="hide-i-dot">
            <rect x={0}          y={0} width={DX - DR - 1} height={H} />  {/* left of dot */}
            <rect x={DX + DR + 1} y={0} width={W}           height={H} />  {/* right of dot */}
            <rect x={0}          y={DY + DR + 1} width={W}  height={H} />  {/* below dot */}
          </clipPath>
        </defs>

        {/* ── "minto" text — clipped so native "i" dot is invisible ── */}
        <text
          x="2" y={BL}
          fontFamily="'Nunito', 'Arial Black', sans-serif"
          fontWeight="900"
          fontSize={FS}
          fill="#d4007f"
          letterSpacing="-1"
          clipPath="url(#hide-i-dot)"
        >
          minto
        </text>

        {/* ── Base dot ── */}
        <circle cx={DX} cy={DY} r={DR} fill="black" />

        {/* ── Phillips screw slots (spin → fade) ── */}
        <g className="ml-slot">
          {/* horizontal */}
          <rect x={DX - DR + 2} y={DY - 1.8} width={(DR - 2) * 2} height={3.6} rx={1.5} fill="white" />
          {/* vertical */}
          <rect x={DX - 1.8} y={DY - DR + 2} width={3.6} height={(DR - 2) * 2} rx={1.5} fill="white" />
        </g>

        {/* ── Smiley (pops in after screw) ── */}
        <g className="ml-smiley">
          {/* Eyes */}
          <circle cx={DX - 4.5} cy={DY - 3} r={3} fill="white" />
          <circle cx={DX + 4.5} cy={DY - 3} r={3} fill="white" />
          {/* Smile */}
          <path
            d={`M ${DX - 7} ${DY + 1} Q ${DX} ${DY + 9} ${DX + 7} ${DY + 1}`}
            stroke="white" strokeWidth={3} fill="none" strokeLinecap="round"
          />
        </g>
      </svg>

      {showSubtitle && (
        <span className={`text-xs font-bold mt-0.5 ${subtitleClassName || 'text-gray-600'}`} style={{ paddingLeft: '3px' }}>
          Phone Repair Ab Minto Mein
        </span>
      )}
    </span>
  );
};

export default MintoLogo;
